import { useEffect, useMemo, useRef, useState } from "react";
import { announce } from "@react-aria/live-announcer";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useFormAPI, useFormStore } from "@/widgets/classic-form/form";
import { FormLevelFeatureType, useMetadataQuery } from "@/entities/form-metadata";
import type { Tree } from "@/entities/tree";
import { useSelectTreeMutation, useTreesQuery } from "@/entities/tree";
import { usePersonSuggestionsQuery } from "@/entities/tree-person";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { useTrackSearchFormInteraction } from "@/shared/lib/ube";
import { Callout } from "@/shared/ui/ancestry";
import { QuickFillCarousel } from "./QuickFillCarousel";
import { ShowUnviewedRecordsOnly } from "./ShowUnviewedRecordsOnly";
import { useQuickFillSelection } from "./useQuickFillSelection";
import styles from "./QuickFillPanel.module.scss";

interface QuickFillPanelProps {
    onDismiss: () => void;
}

export const QuickFillPanel = ({ onDismiss }: QuickFillPanelProps) => {
    const { t } = useTranslation();
    const { data: trees } = useTreesQuery();
    const { data: formMetadata } = useMetadataQuery();
    const { mutate: selectTree } = useSelectTreeMutation();
    const formApi = useFormAPI();
    const { givenName, surname } = useFormStore((s) => s.values.name);
    const selectedPersonId = useFormStore((s) => s.values.treePerson.personId);

    const debouncedGivenName = useDebouncedValue(givenName, 300);
    const debouncedSurname = useDebouncedValue(surname, 300);

    const { data: suggestions, isLoading } = usePersonSuggestionsQuery({
        treeId: trees?.selectedTree?.id,
        givenName: debouncedGivenName,
        surname: debouncedSurname,
    });

    const { selectSuggestion } = useQuickFillSelection();
    const track = useTrackSearchFormInteraction();

    useEffect(() => {
        if (isLoading) {
            announce(t("suggestions.loading"), "polite");
        }
    }, [isLoading, t]);

    const [calloutOpen, setCalloutOpen] = useState(false);
    const treeListRef = useRef<HTMLButtonElement>(null);

    const handleCallout = () => setCalloutOpen((open) => !open);

    const changeTree = (tree: Tree) => {
        track({ action: "change_tree", objectName: "quickfill" });
        selectTree(tree.id);
        formApi.setFieldValue("treePerson", { treeId: "", personId: "" });
        setCalloutOpen(false);
    };

    const showUnviewedRecordsOnlyFeature = useMemo(
        () =>
            formMetadata.features.find(
                (feature) => feature.featureType === FormLevelFeatureType.ShowUnviewedRecordsOnly,
            ),
        [formMetadata.features],
    );

    if (!trees?.selectedTree) return null;

    return (
        <div className={styles.wrapper} data-testid="sfs-quick-fill-panel">
            <div className={styles.header}>
                <div>
                    <h3 className="cardTitle">{t("suggestions.quickFillSuggestion")}</h3>
                    <button
                        ref={treeListRef}
                        type="button"
                        data-testid="sfs-quick-fill-tree-selector"
                        className={classNames(
                            "link calloutTrigger iconAfter iconArrowDownAfter",
                            styles.dropdownButton,
                        )}
                    >
                        {trees.selectedTree.name}
                    </button>
                </div>
                <button
                    type="button"
                    className={classNames("closeBtn", styles.closeButton)}
                    onClick={onDismiss}
                    aria-label={t("suggestions.closeQuickFillSuggestion")}
                />
            </div>

            <Callout trigger={treeListRef} onRequestAction={handleCallout} open={calloutOpen}>
                <div className="calloutMenu" tabIndex={-1}>
                    <ul>
                        {trees?.trees?.map((tree) => (
                            <li key={tree.id}>
                                <button
                                    type="button"
                                    className={classNames("link", {
                                        "calloutMenuChecked iconAfter iconAfterCheck":
                                            trees?.selectedTree?.id === tree.id,
                                    })}
                                    onClick={() => changeTree(tree)}
                                >
                                    <span className={styles.treeName}>{tree.name}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            </Callout>

            {isLoading ? (
                <div className="loading" />
            ) : suggestions?.length ? (
                <>
                    <QuickFillCarousel
                        suggestions={suggestions}
                        treeId={trees.selectedTree.id}
                        onSelect={selectSuggestion}
                    />
                    {showUnviewedRecordsOnlyFeature && selectedPersonId && (
                        <ShowUnviewedRecordsOnly label={showUnviewedRecordsOnlyFeature.attributes.label} />
                    )}
                </>
            ) : (
                <div className={styles.noSuggestions}>{t("suggestions.noSuggestionsFound")}</div>
            )}
        </div>
    );
};
